import React, { useEffect, useState } from 'react';

import Element from '../../../../components/element';
import Flex from '../../../../components/flex';
import Text from '../../../../components/text';
import useStyles from './style';
import NoData from '../../NoData';
import { createRange, updateRange } from '../../Chart/RangeChart/chart';
import {
  get,
  last,
  first,
  cloneDeep,
  has,
  includes,
  flatten,
  min as minimum,
  max as maximum,
  map,
  each,
  round,
  clamp,
  find,
  set
} from 'lodash';

const BloodDraw = ({ healthReportData }) => {
  const classes = useStyles();
  const [showGraph, setShowGraph] = useState(false);

  // find range based on value
  const findSelectedRange = (data, ranges, min, max) => {
    const actualValue = Number(data.value) || 0;
    // actual reading  value is more than max graph range
    if (actualValue >= max) {
      return last(ranges);
    }
    // actual reading  value is lessthan or equal min graph range
    if (actualValue <= min) {
      return first(ranges);
    }

    for (let index = 0; index < ranges.length; index++) {
      const rangeObj = ranges[index];
      if (actualValue >= rangeObj.min && actualValue < rangeObj.max) {
        return rangeObj;
      }
    }
  };

  const normalizeData = org => {
    const data = cloneDeep(org);
    const id = data.__id || '??';
    let ranges = [];
    if (has(data, 'distributionStats')) {
      if (includes(data.__path, 'lipids')) {
        ranges = lipidRanges(data.distributionStats);
      } else {
        ranges = distRanges(data.distributionStats);
      }
    } else {
      // Catch state for the 'default data'
      ranges = data.ranges;
    }

    const candidates = flatten(map(ranges, range => [range.min, range.max]));
    let min = minimum(candidates);
    let max = maximum(candidates);
    const range = findSelectedRange(data, ranges, min, max) || ranges[0];

    const color = org.color;
    const value = round(clamp(data.value, min, max), 2);
    return {
      id: id,
      ranges: ranges,
      selected: range,
      interpretation: range.interpretation || 'N/A',
      min: min,
      max: max,
      color: color,
      value: value,
      unit: data.unit
    };

    function lipidRanges(stats) {
      if (data.__path === 'laboratory.lipids.cholesterol') {
        if (has(data, 'ranges[0].min')) {
          data.ranges[0].min = 200 - 13.33 - 40.235797202411455;
        }
        if (has(data, 'ranges[2].max')) {
          data.ranges[2].max = 240 + 13.33 + 40.235797202411455;
        }
      }
      if (data.__path === 'laboratory.lipids.hdl') {
        if (has(data, 'ranges[0].min')) {
          data.ranges[0].min = 40 - 6.6666 - 12.497959445448435;
        }
        if (has(data, 'ranges[2].max')) {
          data.ranges[2].max = 60 + 6.6666 + 12.497959445448435;
        }
      }
      if (data.__path === 'laboratory.lipids.cholesterol-hdl') {
        if (has(data, 'ranges[0].min')) {
          data.ranges[0].min = 0;
        }
        if (has(data, 'ranges[1].max')) {
          data.ranges[1].max = 10;
        }
      }
      if (data.__path === 'laboratory.lipids.ldl') {
        if (has(data, 'ranges[0].min')) {
          // data.ranges[0].min = 0
        }
        if (has(data, 'ranges[4].max')) {
          data.ranges[4].max = 260;
        }
      }
      if (data.__path === 'laboratory.lipids.trig') {
        if (has(data, 'ranges[3].max')) {
          data.ranges[3].max = 550;
        }
      }
      if (data.__path === 'laboratory.lipids.vldl') {
        if (has(data, 'ranges[0].min')) {
          data.ranges[0].min = 0;
        }
        if (has(data, 'ranges[1].max')) {
          data.ranges[1].max = 60;
        }
      }
      return data.ranges;
    }

    function distRanges(stats, inpValue = 'Normal') {
      if (stats.sd === 0) {
        each(data.ranges, range => set(range, 'color', 'red'));
        return data.ranges; // Catch empty sd
      }
      let baseRange = find(data.ranges, ['interpretation', inpValue]);

      if (!baseRange) {
      } else {
        const halfrange = (baseRange.max - baseRange.min) / 3;

        let min = baseRange.min - halfrange - stats.sd;
        let max = baseRange.max + halfrange + stats.sd;
        if (min < 0) min = 0;
        if (min > data.value) {
          min = data.value;
        }
        if (max < data.value) {
          max = data.value;
        }

        const ranges = [
          {
            interpretation: 'Low',
            color: '#ff3548',
            min: min,
            max: baseRange.min
          },
          baseRange,
          {
            interpretation: 'High',
            color: '#ff3548',
            min: baseRange.max,
            max: max
          }
        ];
        return ranges;
      }

      return data.ranges;
    }
  };

  const renderColor = str => {
    return str && str.includes('High') ? '#e03e52' : '#00b5e2';
  };

  useEffect(() => {
    let data = _.get(healthReportData, 'laboratory');
    data.skipStation ? delete data.skipStation : data;
    const nullValue = null;
    let count = 0;
    _.each(data, (category, tests) => {
      each(category, (key, obj) => {
        var settings = {
          el: 'graph_' + obj,
          margin: { top: 0, right: 0, bottom: 0, left: 0 },
          ranges: key.ranges
        };
        if (key.value !== nullValue) {
          const data = normalizeData(key);
          createRange(settings);
          updateRange(data);
          setShowGraph(false);
        } else {
          createRange(settings);
          count++;
        }
      });
    });
    if (count >= 20) {
      setShowGraph(true);
    }
  }, []);

  return (
    <Element>
      {showGraph ? (
        <NoData />
      ) : (
        <Element className={classes.bloodDrawContainer} marginTop="30px">
          <Flex>
            <Element width="20%">
              <Text family="GTAM" size="14px" color="#222222">
                Electrolytes
              </Text>
            </Element>
            <Element
              width="15%"
              marginTop="3px"
              marginRight="50px"
              textAlign="right"
            >
              <Text family="GTAR" size="12px" color="#222222">
                Sodium (Na+)
              </Text>
            </Element>
            <div
              id="graph_sodium-na"
              style={{ width: '17%', marginRight: '40px', marginTop: '5px' }}
            ></div>
            <Element width="10%" paddingLeft="30px">
              <Text family="GTAM" size="16px" color="#222222">
                {get(
                  healthReportData,
                  'laboratory.electrolytes.sodium-na.value'
                )}
              </Text>
              <Element marginTop="5px">
                <Text
                  family="GTAR"
                  size="12px"
                  color={get(
                    healthReportData,
                    'laboratory.electrolytes.sodium-na.color'
                  )}
                >
                  {get(
                    healthReportData,
                    'laboratory.electrolytes.sodium-na.interpretation'
                  )}
                </Text>
              </Element>
            </Element>
            <Element marginTop="6px">
              <Text family="GTAR" size="12px" color="#959595">
                {get(
                  healthReportData,
                  'laboratory.electrolytes.sodium-na.unit'
                )}
              </Text>
            </Element>
          </Flex>
          <Flex>
            <Element
              width="35%"
              marginTop="3px"
              marginRight="50px"
              textAlign="right"
            >
              <Text family="GTAR" size="12px" color="#222222">
                Potassium (K+)
              </Text>
            </Element>
            <div
              id="graph_potassium-k"
              style={{ width: '17%', marginRight: '40px', marginTop: '5px' }}
            ></div>
            <Element width="10%" paddingLeft="30px">
              <Text family="GTAM" size="16px" color="#222222">
                {get(
                  healthReportData,
                  'laboratory.electrolytes.potassium-k.value'
                )}
              </Text>
              <Element marginTop="5px">
                <Text
                  family="GTAR"
                  size="12px"
                  color={get(
                    healthReportData,
                    'laboratory.electrolytes.potassium-k.color'
                  )}
                >
                  {get(
                    healthReportData,
                    'laboratory.electrolytes.potassium-k.interpretation'
                  )}
                </Text>
              </Element>
            </Element>
            <Element marginTop="6px">
              <Text family="GTAR" size="12px" color="#959595">
                {get(
                  healthReportData,
                  'laboratory.electrolytes.potassium-k.unit'
                )}
              </Text>
            </Element>
          </Flex>
          <Flex>
            <Element
              width="35%"
              marginTop="3px"
              marginRight="50px"
              textAlign="right"
            >
              <Text family="GTAR" size="12px" color="#222222">
                Chloride (Cl-)
              </Text>
            </Element>
            <div
              id="graph_chloride-cl"
              style={{ width: '17%', marginRight: '40px', marginTop: '5px' }}
            ></div>
            <Element width="10%" paddingLeft="30px">
              <Text family="GTAM" size="16px" color="#222222">
                {get(
                  healthReportData,
                  'laboratory.electrolytes.chloride-cl.value'
                )}
              </Text>
              <Element marginTop="5px">
                <Text
                  family="GTAR"
                  size="12px"
                  color={get(
                    healthReportData,
                    'laboratory.electrolytes.chloride-cl.color'
                  )}
                >
                  {get(
                    healthReportData,
                    'laboratory.electrolytes.chloride-cl.interpretation'
                  )}
                </Text>
              </Element>
            </Element>
            <Element marginTop="6px">
              <Text family="GTAR" size="12px" color="#959595">
                {get(
                  healthReportData,
                  'laboratory.electrolytes.chloride-cl.unit'
                )}
              </Text>
            </Element>
          </Flex>
          <Flex className={classes.changeTransform}>
            <Element
              width="35%"
              marginTop="3px"
              marginRight="50px"
              textAlign="right"
            >
              <Text family="GTAR" size="12px" color="#222222">
                Bicarbonate (TCO2)
              </Text>
            </Element>
            <div
              id="graph_bicarbonate-t-co-2"
              style={{ width: '17%', marginRight: '40px', marginTop: '5px' }}
            ></div>
            <Element width="10%" paddingLeft="30px">
              <Text family="GTAM" size="16px" color="#222222">
                {get(
                  healthReportData,
                  'laboratory.electrolytes.bicarbonate-t-co-2.value'
                )}
              </Text>
              <Element marginTop="5px">
                <Text
                  family="GTAR"
                  size="12px"
                  color={get(
                    healthReportData,
                    'laboratory.electrolytes.bicarbonate-t-co-2.color'
                  )}
                >
                  {get(
                    healthReportData,
                    'laboratory.electrolytes.bicarbonate-t-co-2.interpretation'
                  )}
                </Text>
              </Element>
            </Element>
            <Element marginTop="6px">
              <Text family="GTAR" size="12px" color="#959595">
                {get(
                  healthReportData,
                  'laboratory.electrolytes.bicarbonate-t-co-2.unit'
                )}
              </Text>
            </Element>
          </Flex>
          <Flex>
            <Element
              width="35%"
              marginTop="3px"
              marginRight="50px"
              textAlign="right"
            >
              <Text family="GTAR" size="12px" color="#222222">
                Calcium (Ca2+)
              </Text>
            </Element>
            <div
              id="graph_calcium-ca-2"
              style={{ width: '17%', marginRight: '40px', marginTop: '5px' }}
            ></div>
            <Element width="10%" paddingLeft="30px">
              <Text family="GTAM" size="16px" color="#222222">
                {get(
                  healthReportData,
                  'laboratory.electrolytes.calcium-ca-2.value'
                )}
              </Text>
              <Element marginTop="5px">
                <Text
                  family="GTAR"
                  size="12px"
                  color={get(
                    healthReportData,
                    'laboratory.electrolytes.calcium-ca-2.color'
                  )}
                >
                  {get(
                    healthReportData,
                    'laboratory.electrolytes.calcium-ca-2.interpretation'
                  )}
                </Text>
              </Element>
            </Element>
            <Element marginTop="6px">
              <Text family="GTAR" size="12px" color="#959595">
                {get(
                  healthReportData,
                  'laboratory.electrolytes.calcium-ca-2.unit'
                )}
              </Text>
            </Element>
          </Flex>
          <Flex>
            <Element width="20%">
              <Text family="GTAM" size="14px" color="#222222">
                Kidney Function
              </Text>
            </Element>
            <Element
              width="15%"
              marginTop="3px"
              marginRight="50px"
              textAlign="right"
            >
              <Text family="GTAR" size="12px" color="#222222">
                Blood Urea Nitrogen (BUN)
              </Text>
            </Element>
            <div
              id="graph_blood-urea-nitrogen-bun"
              style={{ width: '17%', marginRight: '40px', marginTop: '5px' }}
            ></div>
            <Element width="10%" paddingLeft="30px">
              <Text family="GTAM" size="16px" color="#222222">
                {get(
                  healthReportData,
                  'laboratory.kidney_function.blood-urea-nitrogen-bun.value'
                )}
              </Text>
              <Element marginTop="5px">
                <Text
                  family="GTAR"
                  size="12px"
                  color={get(
                    healthReportData,
                    'laboratory.kidney_function.blood-urea-nitrogen-bun.color'
                  )}
                >
                  {get(
                    healthReportData,
                    'laboratory.kidney_function.blood-urea-nitrogen-bun.interpretation'
                  )}
                </Text>
              </Element>
            </Element>
            <Element marginTop="6px">
              <Text family="GTAR" size="12px" color="#959595">
                {get(
                  healthReportData,
                  'laboratory.kidney_function.blood-urea-nitrogen-bun.unit'
                )}
              </Text>
            </Element>
          </Flex>
          <Flex>
            <Element
              width="35%"
              marginTop="3px"
              marginRight="50px"
              textAlign="right"
            >
              <Text family="GTAR" size="12px" color="#222222">
                Creatinine (CR)
              </Text>
            </Element>
            <div
              id="graph_creatinine-cre"
              style={{ width: '17%', marginRight: '40px', marginTop: '5px' }}
            ></div>
            <Element width="10%" paddingLeft="30px">
              <Text family="GTAM" size="16px" color="#222222">
                {get(
                  healthReportData,
                  'laboratory.kidney_function.creatinine-cre.value'
                )}
              </Text>
              <Element marginTop="5px">
                <Text
                  family="GTAR"
                  size="12px"
                  color={get(
                    healthReportData,
                    'laboratory.kidney_function.creatinine-cre.color'
                  )}
                >
                  {get(
                    healthReportData,
                    'laboratory.kidney_function.creatinine-cre.interpretation'
                  )}
                </Text>
              </Element>
            </Element>
            <Element marginTop="6px">
              <Text family="GTAR" size="12px" color="#959595">
                {get(
                  healthReportData,
                  'laboratory.kidney_function.creatinine-cre.unit'
                )}
              </Text>
            </Element>
          </Flex>
          <Flex>
            <Element width="20%">
              <Text family="GTAM" size="14px" color="#222222">
                Metabolism
              </Text>
            </Element>
            <Element
              width="15%"
              marginTop="3px"
              marginRight="50px"
              textAlign="right"
            >
              <Text family="GTAR" size="12px" color="#222222">
                Glucose (GLU)
              </Text>
            </Element>
            <div
              id="graph_glucose-glu"
              style={{ width: '17%', marginRight: '40px', marginTop: '5px' }}
            ></div>
            <Element width="10%" paddingLeft="30px">
              <Text family="GTAM" size="16px" color="#222222">
                {get(
                  healthReportData,
                  'laboratory.metabolism.glucose-glu.value'
                )}
              </Text>
              <Element marginTop="5px">
                <Text
                  family="GTAR"
                  size="12px"
                  color={get(
                    healthReportData,
                    'laboratory.metabolism.glucose-glu.color'
                  )}
                >
                  {get(
                    healthReportData,
                    'laboratory.metabolism.glucose-glu.interpretation'
                  )}
                </Text>
              </Element>
            </Element>
            <Element marginTop="6px">
              <Text family="GTAR" size="12px" color="#959595">
                {get(
                  healthReportData,
                  'laboratory.metabolism.glucose-glu.unit'
                )}
              </Text>
            </Element>
          </Flex>
          <Flex>
            <Element width="20%">
              <Text family="GTAM" size="14px" color="#222222">
                Liver Function
              </Text>
            </Element>
            <Element
              width="15%"
              marginTop="3px"
              marginRight="50px"
              textAlign="right"
            >
              <Text family="GTAR" size="12px" color="#222222">
                Total Protein (TP)
              </Text>
            </Element>
            <div
              id="graph_tp"
              style={{ width: '17%', marginRight: '40px', marginTop: '5px' }}
            ></div>
            <Element width="10%" paddingLeft="30px">
              <Text family="GTAM" size="16px" color="#222222">
                {get(healthReportData, 'laboratory.liver_function.tp.value')}
              </Text>
              <Element marginTop="5px">
                <Text
                  family="GTAR"
                  size="12px"
                  color={get(
                    healthReportData,
                    'laboratory.liver_function.tp.color'
                  )}
                >
                  {get(
                    healthReportData,
                    'laboratory.liver_function.tp.interpretation'
                  )}
                </Text>
              </Element>
            </Element>
            <Element marginTop="6px">
              <Text family="GTAR" size="12px" color="#959595">
                {get(healthReportData, 'laboratory.liver_function.tp.unit')}
              </Text>
            </Element>
          </Flex>
          <Flex>
            <Element
              width="35%"
              marginTop="3px"
              marginRight="50px"
              textAlign="right"
            >
              <Text family="GTAR" size="12px" color="#222222">
                Albumin (ALB)
              </Text>
            </Element>
            <div
              id="graph_alb"
              style={{ width: '17%', marginRight: '40px', marginTop: '5px' }}
            ></div>
            <Element width="10%" paddingLeft="30px">
              <Text family="GTAM" size="16px" color="#222222">
                {get(healthReportData, 'laboratory.liver_function.alb.value')}
              </Text>
              <Element marginTop="5px">
                <Text
                  family="GTAR"
                  size="12px"
                  color={get(
                    healthReportData,
                    'laboratory.liver_function.alb.color'
                  )}
                >
                  {get(
                    healthReportData,
                    'laboratory.liver_function.alb.interpretation'
                  )}
                </Text>
              </Element>
            </Element>
            <Element marginTop="6px">
              <Text family="GTAR" size="12px" color="#959595">
                {get(healthReportData, 'laboratory.liver_function.alb.unit')}
              </Text>
            </Element>
          </Flex>
          <Flex>
            <Element
              width="35%"
              marginTop="3px"
              marginRight="50px"
              textAlign="right"
            >
              <Text family="GTAR" size="12px" color="#222222">
                Aspartate Aminotransferase (AST)
              </Text>
            </Element>
            <div
              id="graph_ast"
              style={{ width: '17%', marginRight: '40px', marginTop: '5px' }}
            ></div>
            <Element width="10%" paddingLeft="30px">
              <Text family="GTAM" size="16px" color="#222222">
                {get(healthReportData, 'laboratory.liver_function.ast.value')}
              </Text>
              <Element marginTop="5px">
                <Text
                  family="GTAR"
                  size="12px"
                  color={get(
                    healthReportData,
                    'laboratory.liver_function.ast.color'
                  )}
                >
                  {get(
                    healthReportData,
                    'laboratory.liver_function.ast.interpretation'
                  )}
                </Text>
              </Element>
            </Element>
            <Element marginTop="6px">
              <Text family="GTAR" size="12px" color="#959595">
                {get(healthReportData, 'laboratory.liver_function.ast.unit')}
              </Text>
            </Element>
          </Flex>
          <Flex>
            <Element
              width="35%"
              marginTop="3px"
              marginRight="50px"
              textAlign="right"
            >
              <Text family="GTAR" size="12px" color="#222222">
                Alanine Aminotransferase (ALT)
              </Text>
            </Element>
            <div
              id="graph_alt"
              style={{ width: '17%', marginRight: '40px', marginTop: '5px' }}
            ></div>
            <Element width="10%" paddingLeft="30px">
              <Text family="GTAM" size="16px" color="#222222">
                {get(healthReportData, 'laboratory.liver_function.alt.value')}
              </Text>
              <Element marginTop="5px">
                <Text
                  family="GTAR"
                  size="12px"
                  color={get(
                    healthReportData,
                    'laboratory.liver_function.alt.color'
                  )}
                >
                  {get(
                    healthReportData,
                    'laboratory.liver_function.alt.interpretation'
                  )}
                </Text>
              </Element>
            </Element>
            <Element marginTop="6px">
              <Text family="GTAR" size="12px" color="#959595">
                {get(healthReportData, 'laboratory.liver_function.alt.unit')}
              </Text>
            </Element>
          </Flex>
          <Flex>
            <Element
              width="35%"
              marginTop="3px"
              marginRight="50px"
              textAlign="right"
            >
              <Text family="GTAR" size="12px" color="#222222">
                Alkaline Phosphatase (ALP)
              </Text>
            </Element>
            <div
              id="graph_alp"
              style={{ width: '17%', marginRight: '40px', marginTop: '5px' }}
            ></div>
            <Element width="10%" paddingLeft="30px">
              <Text family="GTAM" size="16px" color="#222222">
                {get(healthReportData, 'laboratory.liver_function.alp.value')}
              </Text>
              <Element marginTop="5px">
                <Text
                  family="GTAR"
                  size="12px"
                  color={get(
                    healthReportData,
                    'laboratory.liver_function.alp.color'
                  )}
                >
                  {get(
                    healthReportData,
                    'laboratory.liver_function.alp.interpretation'
                  )}
                </Text>
              </Element>
            </Element>
            <Element marginTop="6px">
              <Text family="GTAR" size="12px" color="#959595">
                {get(healthReportData, 'laboratory.liver_function.alp.unit')}
              </Text>
            </Element>
          </Flex>
          <Flex>
            <Element
              width="35%"
              marginTop="3px"
              marginRight="50px"
              textAlign="right"
            >
              <Text family="GTAR" size="12px" color="#222222">
                Total Bilirubin (TBIL)
              </Text>
            </Element>
            <div
              id="graph_tbil"
              style={{ width: '17%', marginRight: '40px', marginTop: '5px' }}
            ></div>
            <Element width="10%" paddingLeft="30px">
              <Text family="GTAM" size="16px" color="#222222">
                {get(healthReportData, 'laboratory.liver_function.tbil.value')}
              </Text>
              <Element marginTop="5px">
                <Text
                  family="GTAR"
                  size="12px"
                  color={get(
                    healthReportData,
                    'laboratory.liver_function.tbil.color'
                  )}
                >
                  {get(
                    healthReportData,
                    'laboratory.liver_function.tbil.interpretation'
                  )}
                </Text>
              </Element>
            </Element>
            <Element marginTop="6px">
              <Text family="GTAR" size="12px" color="#959595">
                {get(healthReportData, 'laboratory.liver_function.tbil.unit')}
              </Text>
            </Element>
          </Flex>
          <Flex>
            <Element width="20%">
              <Text family="GTAM" size="14px" color="#222222">
                Lipids
              </Text>
            </Element>
            <Element
              width="15%"
              marginTop="3px"
              marginRight="50px"
              textAlign="right"
            >
              <Text family="GTAR" size="12px" color="#222222">
                {get(healthReportData, 'laboratory.lipids.cholesterol.value')
                  ? 'Cholesterol (CHOL)'
                  : 'N/A'}
              </Text>
            </Element>
            <div
              id="graph_cholesterol"
              style={{ width: '17%', marginRight: '40px', marginTop: '5px' }}
            ></div>
            <Element width="10%" paddingLeft="30px">
              <Text family="GTAM" size="16px" color="#222222">
                {get(healthReportData, 'laboratory.lipids.cholesterol.value')}
              </Text>
              <Element marginTop="5px">
                <Text
                  family="GTAR"
                  size="12px"
                  color={
                    get(healthReportData, 'laboratory.lipids.cholesterol.value')
                      ? get(
                          healthReportData,
                          'laboratory.lipids.cholesterol.color'
                        )
                      : ''
                  }
                >
                  {get(healthReportData, 'laboratory.lipids.cholesterol.value')
                    ? get(
                        healthReportData,
                        'laboratory.lipids.cholesterol.interpretation'
                      )
                    : 'N/A'}
                </Text>
              </Element>
            </Element>
            <Element marginTop="6px">
              <Text family="GTAR" size="12px" color="#959595">
                {get(healthReportData, 'laboratory.lipids.cholesterol.value')
                  ? get(healthReportData, 'laboratory.lipids.cholesterol.unit')
                  : '-'}
              </Text>
            </Element>
          </Flex>
          <Flex>
            <Element
              width="35%"
              marginTop="3px"
              marginRight="50px"
              textAlign="right"
            >
              <Text family="GTAR" size="12px" color="#222222">
                {get(healthReportData, 'laboratory.lipids.hdl.value')
                  ? 'High Density Lipoprotein (HDL)'
                  : 'N/A'}
              </Text>
            </Element>
            <div
              id="graph_hdl"
              style={{ width: '17%', marginRight: '40px', marginTop: '5px' }}
            ></div>
            <Element width="10%" paddingLeft="30px">
              <Text family="GTAM" size="16px" color="#222222">
                {get(healthReportData, 'laboratory.lipids.hdl.value')}
              </Text>
              <Element marginTop="5px">
                <Text
                  family="GTAR"
                  size="12px"
                  color={
                    get(healthReportData, 'laboratory.lipids.hdl.value')
                      ? get(healthReportData, 'laboratory.lipids.hdl.color')
                      : ''
                  }
                >
                  {get(healthReportData, 'laboratory.lipids.hdl.value')
                    ? get(
                        healthReportData,
                        'laboratory.lipids.hdl.interpretation'
                      )
                    : 'N/A'}
                </Text>
              </Element>
            </Element>
            <Element marginTop="6px">
              <Text family="GTAR" size="12px" color="#959595">
                {get(healthReportData, 'laboratory.lipids.hdl.value')
                  ? get(healthReportData, 'laboratory.lipids.hdl.unit')
                  : '-'}
              </Text>
            </Element>
          </Flex>
          <Flex>
            <Element
              width="35%"
              marginTop="3px"
              marginRight="50px"
              textAlign="right"
            >
              <Text family="GTAR" size="12px" color="#222222">
                {get(
                  healthReportData,
                  'laboratory.lipids.cholesterol-hdl.value'
                )
                  ? 'Cholesterol/HDL'
                  : 'N/A'}
              </Text>
            </Element>
            <div
              id="graph_cholesterol-hdl"
              style={{ width: '17%', marginRight: '40px', marginTop: '5px' }}
            ></div>
            <Element width="10%" paddingLeft="30px">
              <Text family="GTAM" size="16px" color="#222222">
                {get(
                  healthReportData,
                  'laboratory.lipids.cholesterol-hdl.value'
                )}
              </Text>
              <Element marginTop="5px">
                <Text
                  family="GTAR"
                  size="12px"
                  color={
                    get(
                      healthReportData,
                      'laboratory.lipids.cholesterol-hdl.value'
                    )
                      ? get(
                          healthReportData,
                          'laboratory.lipids.cholesterol-hdl.color'
                        )
                      : ''
                  }
                >
                  {get(
                    healthReportData,
                    'laboratory.lipids.cholesterol-hdl.value'
                  )
                    ? get(
                        healthReportData,
                        'laboratory.lipids.cholesterol-hdl.interpretation'
                      )
                    : 'N/A'}
                </Text>
              </Element>
            </Element>
            <Element marginTop="6px">
              <Text family="GTAR" size="12px" color="#959595">
                {get(
                  healthReportData,
                  'laboratory.lipids.cholesterol-hdl.value'
                )
                  ? get(
                      healthReportData,
                      'laboratory.lipids.cholesterol-hdl.unit'
                    )
                  : '-'}
              </Text>
            </Element>
          </Flex>
          <Flex>
            <Element
              width="35%"
              marginTop="3px"
              marginRight="50px"
              textAlign="right"
            >
              <Text family="GTAR" size="12px" color="#222222">
                {get(healthReportData, 'laboratory.lipids.ldl.value')
                  ? 'Low Density Lipoprotein (LDL)'
                  : 'N/A'}
              </Text>
            </Element>
            <div
              id="graph_ldl"
              style={{ width: '17%', marginRight: '40px', marginTop: '5px' }}
            ></div>
            <Element width="10%" paddingLeft="30px">
              <Text family="GTAM" size="16px" color="#222222">
                {get(healthReportData, 'laboratory.lipids.ldl.value')}
              </Text>
              <Element marginTop="5px">
                <Text
                  family="GTAR"
                  size="12px"
                  color={
                    get(healthReportData, 'laboratory.lipids.ldl.value')
                      ? get(healthReportData, 'laboratory.lipids.ldl.color')
                      : ''
                  }
                >
                  {get(healthReportData, 'laboratory.lipids.ldl.value')
                    ? get(
                        healthReportData,
                        'laboratory.lipids.ldl.interpretation'
                      )
                    : 'N/A'}
                </Text>
              </Element>
            </Element>
            <Element marginTop="6px">
              <Text family="GTAR" size="12px" color="#959595">
                {get(healthReportData, 'laboratory.lipids.ldl.value')
                  ? get(healthReportData, 'laboratory.lipids.ldl.unit')
                  : '-'}
              </Text>
            </Element>
          </Flex>
          <Flex>
            <Element
              width="35%"
              marginTop="3px"
              marginRight="50px"
              textAlign="right"
            >
              <Text family="GTAR" size="12px" color="#222222">
                {get(healthReportData, 'laboratory.lipids.trig.value')
                  ? ' Triglycerides (TRIG)'
                  : 'N/A'}
              </Text>
            </Element>
            <div
              id="graph_trig"
              style={{ width: '17%', marginRight: '40px', marginTop: '5px' }}
            ></div>
            <Element width="10%" paddingLeft="30px">
              <Text family="GTAM" size="16px" color="#222222">
                {get(healthReportData, 'laboratory.lipids.trig.value')}
              </Text>
              <Element marginTop="5px">
                <Text
                  family="GTAR"
                  size="12px"
                  color={
                    get(healthReportData, 'laboratory.lipids.trig.value')
                      ? get(healthReportData, 'laboratory.lipids.trig.color')
                      : ''
                  }
                >
                  {get(healthReportData, 'laboratory.lipids.trig.value')
                    ? get(
                        healthReportData,
                        'laboratory.lipids.trig.interpretation'
                      )
                    : 'N/A'}
                </Text>
              </Element>
            </Element>
            <Element marginTop="6px">
              <Text family="GTAR" size="12px" color="#959595">
                {get(healthReportData, 'laboratory.lipids.trig.value')
                  ? get(healthReportData, 'laboratory.lipids.trig.unit')
                  : '-'}
              </Text>
            </Element>
          </Flex>
          <Flex>
            <Element
              width="35%"
              marginTop="3px"
              marginRight="50px"
              textAlign="right"
            >
              <Text family="GTAR" size="12px" color="#222222">
                {get(healthReportData, 'laboratory.lipids.vldl.value')
                  ? 'Very Low Density Lipoprotein (VLDL)'
                  : 'N/A'}
              </Text>
            </Element>
            <div
              id="graph_vldl"
              style={{ width: '17%', marginRight: '40px', marginTop: '5px' }}
            ></div>
            <Element width="10%" paddingLeft="30px">
              <Text family="GTAM" size="16px" color="#222222">
                {get(healthReportData, 'laboratory.lipids.vldl.value')}
              </Text>
              <Element marginTop="5px">
                <Text
                  family="GTAR"
                  size="12px"
                  color={
                    get(healthReportData, 'laboratory.lipids.vldl.value')
                      ? get(healthReportData, 'laboratory.lipids.vldl.color')
                      : ''
                  }
                >
                  {get(healthReportData, 'laboratory.lipids.vldl.value')
                    ? get(
                        healthReportData,
                        'laboratory.lipids.vldl.interpretation'
                      )
                    : 'N/A'}
                </Text>
              </Element>
            </Element>
            <Element marginTop="6px">
              <Text family="GTAR" size="12px" color="#959595">
                {get(healthReportData, 'laboratory.lipids.vldl.value')
                  ? get(healthReportData, 'laboratory.lipids.vldl.unit')
                  : '-'}
              </Text>
            </Element>
          </Flex>
        </Element>
      )}
    </Element>
  );
};

export default BloodDraw;
